// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useCallback, useState } from "react";
import { connect, useSelector } from "react-redux";

import LoadingState from "src/components/LoadingState";
import OutsideClickHandler from "src/components/OutsideClickHandler";
import Title from "src/components/chatroom/Header/Title";
import Members from "src/components/chatroom/Members";
import Status from "src/components/Unifize/Status";
import PreviewModal from "src/components/file/PreviewModal";
import Dock from "src/containers/CurrentRoomDock";
import Files from "src/containers/chatroom/Files";
import Owner from "src/containers/chatroom/Owner";
import Icon from "src/icons";
import {
  getCurrentChatRoom,
  getFileUploadModalStatus,
  getIsNavigatedFromHeader,
  getMessageData,
  getReplyTo,
  getMandatoryFieldCount,
  isStatusDisabled,
} from "src/reducers";
import appLocation from "src/constants/location";
import * as colors from "src/styles/constants/colors";

import AppInfo from "../AppInfo";
import {
  AppInfoWrapper,
  CloseIcon,
  HeaderItems,
  Header,
  Main,
  MenuTooltip,
  PanelWrapper,
  Pointer,
  TitleWrapper,
} from "./styles";
import Tabs from "./Tabs";

import type { AppState, Reply, UnifizeChatRoom, UnifizeUser } from "src/types";

type Props = {
  room: UnifizeChatRoom,
  activeDock: string,
  vh: number,
  showFilesPreviewModal: boolean,
  currentUser: UnifizeUser,
  isStatusDisabled: boolean,
  navigatedFromHeader: boolean,
};

const Mobile = ({
  room,
  activeDock,
  vh,
  showFilesPreviewModal,
  currentUser,
}: Props) => {
  const [registerScreen, setRegisterScreen] = useState(false);
  const [showMenuTooltip, setMenuTooltip] = useState(true);

  const showRegister = useCallback(() => setRegisterScreen(true), []);
  const hideRegister = useCallback(() => setRegisterScreen(false), []);

  const closeMenuTooltip = useCallback(() => setMenuTooltip(false), []);

  const mandatoryFieldCount = useSelector(({ app }) =>
    getMandatoryFieldCount(app),
  );

  if (activeDock === "files")
    return (
      <PanelWrapper height={vh}>
        <Dock isSrw showClose />
      </PanelWrapper>
    );

  if (registerScreen)
    return (
      <AppInfoWrapper height={vh}>
        <CloseIcon>
          <Icon
            type="close"
            color={colors.primary}
            handleClick={hideRegister}
          />
        </CloseIcon>
        <AppInfo />
      </AppInfoWrapper>
    );

  return (
    <Main height={vh}>
      {showFilesPreviewModal && room?.id && <PreviewModal roomId={room.id} />}
      {room && room.id ? (
        <Header>
          <TitleWrapper>
            <Icon type="menu" handleClick={showRegister} />
            <Title roomId={room.id} location="Chatroom Header" />
            {showMenuTooltip && (
              <OutsideClickHandler onClickOutside={closeMenuTooltip}>
                <MenuTooltip>
                  <Pointer />
                  {i18n.t(k.CLICK_HERE_TO_SIGN_UP_OR_LOG_I)}
                  <Icon
                    type="close"
                    color={colors.brightLight}
                    handleClick={closeMenuTooltip}
                  />
                </MenuTooltip>
              </OutsideClickHandler>
            )}
          </TitleWrapper>
          <HeaderItems>
            <Status roomId={room.id} location={appLocation.chatroomHeader} />
            <Owner roomId={room.id} location={appLocation.chatroomHeader} />
            <Members
              roomId={room.id}
              location={appLocation.chatroomHeader}
              address={room.address}
            />
            <Files roomId={room.id} isSrw showPanel />
          </HeaderItems>
        </Header>
      ) : (
        <LoadingState type="header" />
      )}
      <Tabs
        room={room}
        mandatoryFieldCount={mandatoryFieldCount}
        currentUserUid={currentUser.uid}
      />
    </Main>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => {
  const replyTo: Reply = getReplyTo(app);
  const { to } = replyTo;
  const room = getCurrentChatRoom(app);
  return {
    currentUser: app.currentUser,
    room,
    activeDock: app.activeDock.srw,
    currentMessage: getMessageData(app, to),
    showFilesPreviewModal: room
      ? getFileUploadModalStatus(app, room.id)
      : false,
    isStatusDisabled: room ? isStatusDisabled(app, room.id) : false,
    navigatedFromHeader: getIsNavigatedFromHeader(app),
  };
};

export default connect(mapStateToProps, {})(Mobile);
